import { FormattedMessage, useIntl } from 'react-intl';
import { Field, ErrorMessage } from 'formik';
import Heading from '../../../common/Heading';
import formStyles from '../../../styles/forms.module.scss';
import s from './StepGreenPowerData.module.scss';
import {
  TextInput,
  RadioGroupInput,
  CheckboxInput,
  AccessibleDatepickerInput,
  type RadioGroupOption,
} from '../../../common/FormFields';
import InputErrorMessage from '../../../common/FormFields/InputErrorMessage';
import checkboxStyles from '../FacilityForm/WindFacilityForm.module.scss';
import InfoBubble from '../../../common/InfoBubble/InfoBubble';
import InfoBox from '../../../common/InfoBox/InfoBox';
import {
  getControlAreaRadioOptions,
  getGreenGreyPowerProductOptions,
  isCustomControlArea,
  type GreenPowerField,
} from '../../../domain/facility';
import type { WizardStepProps } from '../wizard/types/wizardTypes';

const NAME_PREFIX = 'greenPowerData.';

const POWER_CONTROL_AREA_OTHER_MSG_ID =
  'offerWizard.stepGreenPowerData.controlArea.other';

type StepGreenPowerDataProps = Pick<
  WizardStepProps,
  'values' | 'isGreenPowerConsumptionTooSmall'
>;

function StepGreenPowerData({
  values,
  isGreenPowerConsumptionTooSmall,
}: StepGreenPowerDataProps) {
  const intl = useIntl();
  const fieldName = (field: GreenPowerField) => `${NAME_PREFIX}${field}`;

  const isControlAreaCustom = isCustomControlArea(
    values.greenPowerData?.controlArea
  );

  const linkToVerbundProductPortfolio =
    intl.locale === 'en'
      ? 'https://www.verbund.com/en-at'
      : 'https://www.verbund.com/de-at';

  const certificationOptions: RadioGroupOption[] = [
    {
      value: 'ee',
      labelText: 'TÜV Süd EE',
      infoText: intl.formatMessage({
        id: 'offerWizard.stepGreenPowerData.certification.TUEVEE',
      }),
    },
    {
      value: 'ee_plus',
      labelText: 'TÜV Süd EE+',
      infoText: intl.formatMessage({
        id: 'offerWizard.stepGreenPowerData.certification.TUEVEEPlus',
      }),
    },
    {
      value: 'ee_new',
      labelText: 'TÜV Süd EE neu',
      infoText: intl.formatMessage({
        id: 'offerWizard.stepGreenPowerData.certification.TUEVEENeu',
      }),
    },
    {
      value: 'none',
      labelText: intl.formatMessage({
        id: 'offerWizard.stepGreenPowerData.certification.none',
      }),
    },
  ];

  const originOptions: RadioGroupOption[] = [
    { value: 'de', labelText: 'Deutschland' },
    { value: 'de_at_ch', labelText: 'D-A-CH' },
    { value: 'eu', labelText: 'Europa' },
    { value: 'at', labelText: 'Österreich' },
    { value: 'de_at_ch_fr', labelText: 'D-A-CH + Frankreich' },
    {
      value: 'no_specification',
      labelText: intl.formatMessage({
        id: 'offerWizard.stepGreenPowerData.origin.noSpecification',
      }),
    },
  ];

  const controlAreaOptions = getControlAreaRadioOptions(
    intl,
    POWER_CONTROL_AREA_OTHER_MSG_ID
  );
  const productOptions = getGreenGreyPowerProductOptions();

  return (
    <>
      <Heading level={2} className={s.heading}>
        <FormattedMessage id="offerWizard.stepGreenPowerData.title" />
      </Heading>
      <div className={formStyles.inputWithVerticalSpacing}>
        <div className={formStyles.inputPair}>
          <Field
            name="information.start"
            labelMsg="offerWizard.StepSomeInformation.start.label"
            component={AccessibleDatepickerInput}
            minDate={new Date()}
          />
          <Field
            name="information.end"
            labelMsg="offerWizard.StepSomeInformation.end.label"
            component={AccessibleDatepickerInput}
            minDate={() => {
              if (values.information?.start) {
                return new Date(values.information.start);
              }
              return new Date();
            }}
          />
        </div>
        <div className={checkboxStyles.checkBoxWithInfoIcon}>
          <CheckboxInput
            name={fieldName('swap')}
            labelContent={
              <FormattedMessage id="offerWizard.stepPowerType.gruenstromSwap" />
            }
          />
          <div className={checkboxStyles.infoIcon}>
            <InfoBubble>
              <FormattedMessage id="offerWizard.stepPowerType.gruenstromSwap.infoBubble" />
            </InfoBubble>
          </div>
        </div>
      </div>
      <div className={formStyles.inputWithVerticalSpacing}>
        <fieldset>
          <legend className={s.groupLabel}>
            <FormattedMessage id="offerWizard.stepGreenPowerData.preferredPowerGeneration" />
          </legend>
          <div className={s.generationRadios}>
            <CheckboxInput
              name={fieldName('wind')}
              labelContent={
                <FormattedMessage id="offerWizard.stepFacilityType.wind" />
              }
            />
            <CheckboxInput
              name={fieldName('water')}
              labelContent={
                <FormattedMessage id="offerWizard.stepFacilityType.wasser" />
              }
            />
            <CheckboxInput
              name={fieldName('photovoltaic')}
              labelContent={
                <FormattedMessage id="offerWizard.stepFacilityType.photovoltaik" />
              }
            />
            <CheckboxInput
              name={fieldName('biomass')}
              labelContent={
                <FormattedMessage id="offerWizard.stepFacilityType.biomasse" />
              }
            />
          </div>
        </fieldset>
        <ErrorMessage
          name={`${NAME_PREFIX}powerGeneration`}
          component={InputErrorMessage}
        />
      </div>
      <div className={formStyles.inputPairSingle}>
        <Field
          name={fieldName('consumption')}
          component={TextInput}
          labelMsg="offerWizard.stepGreenPowerData.consumption.label"
        />
      </div>
      <div role="alert">
        {isGreenPowerConsumptionTooSmall && (
          <InfoBox>
            <Heading level={4}>
              <FormattedMessage id="offerWizard.stepGreenPowerData.isConsumptionTooSmall.title" />
            </Heading>
            <p className={s.consumptionTooSmallText}>
              <FormattedMessage id="offerWizard.stepGreenPowerData.isConsumptionTooSmall.text1" />{' '}
              <a
                href={linkToVerbundProductPortfolio}
                target="_blank"
                rel="noreferrer"
              >
                <FormattedMessage id="offerWizard.stepGreenPowerData.isConsumptionTooSmall.link" />
              </a>
            </p>
          </InfoBox>
        )}
      </div>
      <div className={formStyles.inputWithVerticalSpacing}>
        <Field
          captionMsg="offerWizard.stepGreenPowerData.certification"
          name={fieldName('certification')}
          options={certificationOptions}
          component={RadioGroupInput}
          radioButtonsWrapperClass={s.certificationRadios}
        />
      </div>
      <div className={formStyles.inputPairSingle}>
        <Field
          name={fieldName('label')}
          component={TextInput}
          labelMsg="offerWizard.stepGreenPowerData.label.label"
        />
      </div>
      <Field
        captionMsg="offerWizard.stepGreenPowerData.origin"
        name={fieldName('regionOfOrigin')}
        options={originOptions}
        component={RadioGroupInput}
        radioButtonsWrapperClass={s.originRadios}
      />
      <div className={formStyles.inputPairSingle}>
        <Field
          name={fieldName('numberOfMeterPoints')}
          component={TextInput}
          labelMsg="offerWizard.stepGreenPowerData.numberOfMeterPoints.label"
        />
      </div>
      <div className={formStyles.inputWithVerticalSpacing}>
        <Field
          captionMsg="offerWizard.stepGreenPowerData.controlArea"
          name={fieldName('controlArea')}
          options={controlAreaOptions}
          component={RadioGroupInput}
          radioButtonsWrapperClass={s.controlAreaRadios}
        />
        {isControlAreaCustom && (
          <div className={formStyles.inputPairSingle}>
            <Field
              name={fieldName('customControlArea')}
              component={TextInput}
              labelMsg="offerWizard.stepGreenPowerData.controlArea.otherTextInput"
              required
            />
          </div>
        )}
      </div>
      <div className={formStyles.inputWithVerticalSpacing}>
        <Field
          captionMsg="offerWizard.stepGreenPowerData.product"
          name={fieldName('product')}
          options={productOptions}
          component={RadioGroupInput}
          radioButtonsWrapperClass={s.productRadios}
        />
      </div>
    </>
  );
}

export default StepGreenPowerData;
